import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import ButtonBase from '@material-ui/core/ButtonBase';
import { alpha, makeStyles } from '@material-ui/core/styles';

import {
  AI_GLOW_BEAT,
  AI_GLOW_FROM,
  AI_GLOW_TO,
  AI_GLOW_TURN,
  AI_GLOW_VIA,
} from 'components/theme/aiGlow';
import {
  ROUNDED_CONTROL_RADIUS,
  ROUNDED_CONTROL_RADIUS_SMALL,
} from 'components/theme/controlRadius';
import { capitalize } from 'utils/helpers';

const useStyles = makeStyles((theme) => ({
  root: {
    ...theme.typography.fontPanton,
    borderRadius: 4,
    boxSizing: 'border-box',
    color: (props) =>
      props.outline
        ? theme.palette.text.secondary
        : theme.palette.textWhite.secondary,
    fontSize: '0.9rem',
    //lineHeight: 1.1,
    minHeight: theme.spacing(5),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(0.75) + 2,
    textAlign: 'center',
    transition: theme.transitions.create(['box-shadow', 'color']),
    '&:hover': {
      //boxShadow: theme.shadows[1],
      color: (props) =>
        props.outline ? theme.palette.buttonHoverText : theme.palette.textWhite,
      '& $backface': {
        opacity: 0.1,
        borderRadius: 4,
      },
    },
    '&:active': {
      boxShadow: theme.shadows[4],
    },
  },
  small: {
    borderRadius: 3,
    fontSize: '0.75rem',
    minHeight: theme.spacing(3.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(0.5) + 1,
    '& $backface': {
      borderRadius: 3,
    },
  },
  backface: {
    backgroundColor: 'rgb(0,0,0)',
    bottom: 0,
    left: 0,
    right: 0,
    opacity: 0,
    position: 'absolute',
    top: 0,
    transition: theme.transitions.create('opacity'),
    zIndex: 0,
  },
  elevated: {
    boxShadow: theme.shadows[1],
  },
  colorDefault: {
    backgroundColor: theme.palette.buttonPrimary,
  },
  colorPrimary: {
    backgroundColor: theme.palette.buttonPrimary,
  },
  colorSecondary: {
    backgroundColor: theme.palette.buttonSecondary,
  },
  colorMain: {
    backgroundColor: theme.palette.buttonMain,
  },
  colorDark: {
    backgroundColor: theme.palette.buttonDark,
  },
  colorDarker: {
    backgroundColor: theme.palette.buttonDarker,
  },
  colorLight: {
    backgroundColor: theme.palette.buttonLight,
  },
  colorLighter: {
    backgroundColor: theme.palette.buttonLighter,
  },
  colorDarkGrey: {
    backgroundColor: theme.palette.grey['800'],
  },
  colorGrey: {
    backgroundColor: theme.palette.grey['600'],
  },
  colorDanger: {
    backgroundColor: theme.palette.danger.main,
  },
  colorTrash: {
    backgroundColor: theme.palette.trash.main,
  },
  colorOutlineDefault: {
    borderColor: theme.palette.buttonPrimary,
  },
  colorOutlinePrimary: {
    borderColor: theme.palette.buttonPrimary,
  },
  colorOutlineSecondary: {
    borderColor: theme.palette.buttonSecondary,
  },
  colorOutlineMain: {
    borderColor: theme.palette.buttonMain,
  },
  colorOutlineDark: {
    borderColor: theme.palette.buttonDark,
  },
  colorOutlineDarker: {
    borderColor: theme.palette.buttonDarker,
  },
  colorOutlineLight: {
    borderColor: theme.palette.buttonLight,
  },
  colorOutlineLighter: {
    borderColor: theme.palette.buttonLighter,
  },
  colorOutlineDarkGrey: {
    borderColor: theme.palette.grey['800'],
  },
  colorOutlineGrey: {
    borderColor: theme.palette.grey['600'],
  },
  colorOutlineDanger: {
    borderColor: theme.palette.danger.main,
  },
  colorOutlineTrash: {
    borderColor: theme.palette.trash.main,
  },
  colorOutlineInput: {
    borderColor:
      theme.name === 'light' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
  },
  fullHeight: {
    height: '100%',
  },
  fullWidth: {
    width: '100%',
  },
  disabled: {
    opacity: 0.5,
  },
  // Softer corners (off by default): a scene that wants its controls rounder
  // opts in per button, the rest of Lixani keeps the 4px.
  rounded: {
    borderRadius: ROUNDED_CONTROL_RADIUS,
    // Also while hovered: the root's hover rule gives the backface the
    // square 4px corner with one pseudo-class more than `& $backface`.
    '& $backface, &:hover $backface': {
      borderRadius: ROUNDED_CONTROL_RADIUS,
    },
    '&$small': {
      borderRadius: ROUNDED_CONTROL_RADIUS_SMALL,
      '& $backface, &:hover $backface': {
        borderRadius: ROUNDED_CONTROL_RADIUS_SMALL,
      },
    },
  },
  outline: {
    borderStyle: 'solid',
    borderWidth: 1,
    //color: theme.palette.text.main,
  },
  // The AI launcher's glow: a cyan-to-violet ring turning over a halo that
  // breathes, with the sparkle beating along with the halo. Two clocks on
  // purpose — see the note in components/theme/aiGlow. The ring is painted
  // on the outer 2px of the button's own box and takes the button's corner
  // (4px, or the 12px of `rounded`), so a glowing button is exactly the size
  // and shape of the buttons beside it; only the halo, a soft light with no
  // solid rim, reaches outside.
  glow: {
    position: 'relative',
    '& svg': {
      animation: `$lixButtonSparkleTwinkle ${AI_GLOW_BEAT} ease-in-out infinite`,
      // The big star of the sparkle, not the middle of its box.
      transformOrigin: '40% 55%',
    },
    '&:hover $glowHalo': {
      animation: `$lixButtonGlowBreatheHover ${AI_GLOW_BEAT} ease-in-out infinite`,
    },
    '@media (prefers-reduced-motion: reduce)': {
      '& svg': {
        animation: 'none',
      },
    },
  },
  // A layer of its own rather than the button's own box-shadow, which has to
  // stay free for the elevation ButtonBase raises while the button is pressed.
  glowHalo: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: 'inherit',
    pointerEvents: 'none',
    zIndex: 0,
    boxShadow: `0 0 12px 2px ${alpha(theme.palette.buttonLightest, 0.5)}`,
    animation: `$lixButtonGlowBreathe ${AI_GLOW_BEAT} ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  // The ring the sweep runs in: the outer 2px of the button's box, masked
  // down to that track alone so the button's own face and the content stay
  // untouched (the outlined variant is see-through). It inherits the corner
  // the button has rather than rounding it further.
  glowRing: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: 'inherit',
    padding: 2,
    boxSizing: 'border-box',
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 1,
    WebkitMask:
      'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
    WebkitMaskComposite: 'xor',
    mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
    maskComposite: 'exclude',
  },
  // A square wider than the ring's diagonal, centred on it, carrying the whole
  // gradient; turning it turns the colour around the outline. Stands still for
  // people who asked for less motion.
  glowSweep: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '340%',
    height: 0,
    paddingBottom: '340%',
    background: `conic-gradient(from 0deg, ${AI_GLOW_FROM}, ${AI_GLOW_VIA}, ${AI_GLOW_TO}, ${AI_GLOW_VIA}, ${AI_GLOW_FROM})`,
    transform: 'translate(-50%, -50%) rotate(0deg)',
    animation: `$lixButtonGlowSweep ${AI_GLOW_TURN} linear infinite`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  '@keyframes lixButtonGlowSweep': {
    from: {
      transform: 'translate(-50%, -50%) rotate(0deg)',
    },
    to: {
      transform: 'translate(-50%, -50%) rotate(360deg)',
    },
  },
  '@keyframes lixButtonGlowBreathe': {
    '0%, 100%': {
      boxShadow: `0 0 12px 2px ${alpha(theme.palette.buttonLightest, 0.5)}`,
    },
    '50%': {
      boxShadow: `0 0 18px 4px ${alpha(theme.palette.buttonLightest, 0.72)}`,
    },
  },
  '@keyframes lixButtonGlowBreatheHover': {
    '0%, 100%': {
      boxShadow: `0 0 16px 4px ${alpha(theme.palette.buttonLightest, 0.62)}`,
    },
    '50%': {
      boxShadow: `0 0 24px 6px ${alpha(theme.palette.buttonLightest, 0.85)}`,
    },
  },
  '@keyframes lixButtonSparkleTwinkle': {
    '0%, 100%': {
      transform: 'scale(1) rotate(0deg)',
      opacity: 0.9,
    },
    '50%': {
      transform: 'scale(1.16) rotate(18deg)',
      opacity: 1,
    },
  },
}));

const Button = React.forwardRef(function Button(props, ref) {
  const classes = useStyles({ outline: props.outline });

  const {
    children,
    className: classNameProp,
    color,
    disableBackface,
    elevated,
    fullHeight,
    fullWidth,
    glow,
    outline,
    rounded,
    to,
    size,
    variant: _variant,
    ...rootProps
  } = props;

  const className = clsx(
    classes.root,
    color && !outline && classes[`color${capitalize(color)}`],
    color && outline && classes[`colorOutline${capitalize(color)}`],
    outline && classes.outline,
    classNameProp,
    elevated && classes.elevated,
    fullWidth && classes.fullWidth,
    fullHeight && classes.fullHeight,
    props.disabled && classes.disabled,
    size === 'small' && classes.small,
    rounded && classes.rounded,
    glow && classes.glow
  );

  let linkProps = {};
  if (to) {
    linkProps.component = Link;
    linkProps.to = to;
  }

  return (
    <ButtonBase {...rootProps} className={className} ref={ref} {...linkProps}>
      {glow && (
        <span
          className={classes.glowHalo}
          aria-hidden="true"
          data-button-glow-halo="true"
        />
      )}
      {!disableBackface && <span className={classes.backface} />}
      {children}
      {glow && (
        <span
          className={classes.glowRing}
          aria-hidden="true"
          data-button-glow="true"
        >
          <span className={classes.glowSweep} data-button-glow-sweep="true" />
        </span>
      )}
    </ButtonBase>
  );
});

Button.displayName = 'Button';

Button.propTypes = {
  className: PropTypes.string,
  classNameProp: PropTypes.string,
  children: PropTypes.node,
  color: PropTypes.string,
  disabled: PropTypes.bool,
  disableBackface: PropTypes.bool,
  elevated: PropTypes.bool,
  fullHeight: PropTypes.bool,
  fullWidth: PropTypes.bool,
  /** The assistant's treatment: a turning ring on the button's own outline and a breathing halo, in the button's own size and corner (off by default). */
  glow: PropTypes.bool,
  onClick: PropTypes.func,
  outline: PropTypes.bool,
  rootProps: PropTypes.object,
  /** Softer corners (12px, 10px small) instead of the default 4px. */
  rounded: PropTypes.bool,
  size: PropTypes.string,
  to: PropTypes.string,
};

export default Button;
